<template>
  <div>
    <confirm-modal
      v-if="showExcelDownloadModal"
      id="excel-download-modal"
      title="Information"
      :confirmation="confirmModalMessage"
      ok-button="Close"
      close-button=""
      @submit="showExcelDownloadModal = false"
    />
    <submit-declarations-modal
      v-if="shipment.id && canEdit"
      id="modal-submit-declarations"
      :resendables="resendables"
      :shipment="shipment"
      :selected-parcels="selectedParcels"
      :selected-containers="selectedContainers"
      :selected-all="selectedAll"
      :declaration-type="declarationType"
      @submit="submit"
    />
    <confirm-modal
      id="require-confirmation-modal"
      title="Are you sure you want to proceed?"
      confirmation="ENS not fully registered"
      description="Some ENS filings for this shipment are not yet registered! H7 declarations will fail at the moment if you rely on x7trade ENS filings submission. Navigate back to shipment view and make sure ENS filings are registered. Proceed currently only if the process relies on some other previous document."
      title-class="text-light"
      header-bg-variant="warning"
      ok-button-styles="outline-secondary"
      @submit="handleEnsIncompleteConfirmation"
    />
    <b-row>
      <b-col
        class="mt-2"
        sm="5"
        md="5"
        lg="4"
      >
        <b-card-text v-if="resendables">
          Parcels that can be sent to customs again.
        </b-card-text>
        <b-card-text v-else>
          Parcels not sent to customs yet.
        </b-card-text>
      </b-col>
      <b-col class="text-right">
        <b-button
          variant="outline-success"
          class="m-1"
          :disabled="excelLoading"
          @click="handleExcelDownload"
        >
          Download as Excel
          <b-spinner
            v-if="excelLoading"
            small
            class="ml-1"
          />
        </b-button>
        <b-button
          v-if="!resendables"
          :disabled="containers.length === 0 || warehouseExcelLoading"
          variant="outline-success"
          @click="handleWarehouseExcelDownload"
        >
          Download Excel for warehouse
          <b-spinner
            v-if="warehouseExcelLoading"
            small
            class="ml-1"
          />
        </b-button>
        <b-button
          v-if="canEdit"
          variant="outline-success"
          class="m-1"
          :disabled="disabledH7Btn"
          @click="clickSendBtn"
        >
          Send {{ declarationType.toUpperCase() }} declarations
        </b-button>
        <b-alert
          variant="danger"
          :show="errorIssues.length > 0"
        >
          <div class="alert-body">
            Please repair manifest file before sending declaration
          </div>
        </b-alert>
        <b-alert
          variant="warning"
          :show="!isDeclarant"
        >
          <div class="alert-body">
            You don't have Declarant permissions to submit customs declarations.
          </div>
        </b-alert>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <parcels-tree
          :shipment="shipment"
          :containers="containers"
          :data-loading="isLoading"
          data-loading-message="Loading..."
          :filter="resendables ? 'resendables' : 'notSent'"
          :filters="filters"
          :declaration-type="declarationType"
          :show-edit="canEdit"
          @sortDesc="fetchData(null, true, $event)"
          @selection="selection"
          @parcels="updateParcelsCollection"
          @updateFilters="applyFilters"
          @deleteParcel="deleteParcel"
        />

      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BAlert, BButton, BCardText, BCol, BRow, BSpinner } from 'bootstrap-vue'
import ParcelsTree from '@/views/shipments/declarations/ParcelsTree.vue'
import SubmitDeclarationsModal from '@/views/shipments/declarations/SubmitDeclarationsModal.vue'
import shipmentModel from '@/views/shipments/shipmentModel'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import handleError from '@/views/components/errorHandler'
import { EventBus } from '@core/utils/utils'
import { searchQuery } from '@core/utils/filter'
import ConfirmModal from '@core/components/confirmation/confirmModal.vue'
import excelDownloadMixin from '@/mixins/excelDownloadMixin'

export default {
  components: {
    BSpinner,
    ConfirmModal,
    BButton,
    BCardText,
    BAlert,
    BCol,
    BRow,
    ParcelsTree,
    SubmitDeclarationsModal,
  },
  mixins: [excelDownloadMixin],
  props: {
    shipmentId: {
      type: String,
      default: null,
    },
    filters: {
      type: Object,
      default: () => {},
    },
    issues: {
      type: Array,
      default: () => [],
    },
    resendables: {
      type: Boolean,
      default: false,
    },
    declarationType: {
      type: String,
      default: 'h7',
    },
    requireConfirmation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      shipment: {},
      containers: [],
      selectedAll: false,
      selectedContainers: [],
      selectedParcels: [],
      allParcelsData: [],
      isLoading: false,
      selectedContainNotArrivedParcels: false,
      localFilters: {},
    }
  },
  computed: {
    isDeclarant() {
      return this.$permissions().hasPermission('create declarations')
    },
    canEdit() {
      return this.$permissions().hasPermission('edit shipment')
    },
    disabledH7Btn() {
      return (this.selectedAll === false && this.selectedContainers.length === 0 && this.selectedParcels.length === 0)
        || this.errorIssues.length > 0 || !this.isDeclarant
    },
    errorIssues() {
      return this.issues.filter(x => x.type === 'error')
    },
    companyId() {
      return this.$data.shipment.company.id
    },
    downloadUrl() {
      if (this.resendables) {
        return `${process.env.VUE_APP_API_URL}/v1/companies/${this.companyId}/shipments/${this.shipmentId}/declarations/${this.declarationType}/resendables`
      }
      return `${process.env.VUE_APP_API_URL}/v1/companies/${this.companyId}/shipments/${this.shipmentId}/declarations/${this.declarationType}/notSent`
    },
    excelDownloadUrl() {
      return `${this.downloadUrl}/excel`
    },
    warehouseExcelUrl() {
      return `${this.downloadUrl}/excel-for-warehouse`
    },
  },
  async created() {
    this.localFilters = { ...this.filters }
    this.getShipment().then(() => this.fetchData())
    EventBus.$on('general-declaration-update', this.fetchData)
  },
  beforeDestroy() {
    EventBus.$off('general-declaration-update', this.fetchData)
  },
  methods: {
    clickSendBtn() {
      if (this.requireConfirmation) {
        this.$bvModal.show('require-confirmation-modal')
      } else {
        this.$bvModal.show('modal-submit-declarations')
      }
    },
    handleEnsIncompleteConfirmation() {
      this.$bvModal.hide('require-confirmation-modal')
      this.$bvModal.show('modal-submit-declarations')
    },
    getShipment() {
      return new Promise(resolve => {
        const cachedData = shipmentModel().getCachedShipment(this.shipmentId)
        if (cachedData === null) {
          shipmentModel()
            .getShipment(this.shipmentId, this)
            .then(response => {
              shipmentModel().cacheShipment(response.data)
              this.$set(this, 'shipment', response.data)
              resolve() // Resolve after shipment is fetched
            })
        } else {
          this.$set(this, 'shipment', cachedData)
          resolve() // Resolve immediately if data is cached
        }
      })
    },
    fetchData(activeTab, showLoader = true, sortDesc = null) {
      if ((!activeTab || activeTab === 'not-sent') && !this.isLoading) {
        if (showLoader) {
          this.isLoading = true
        }
        let url = `/v1/companies/${this.companyId}/shipments/${this.shipmentId}/declarations/${this.declarationType}/not-sent`
        if (this.resendables) {
          url = `/v1/companies/${this.companyId}/shipments/${this.shipmentId}/declarations/resendables`
        }
        this.$http.get(searchQuery(url, this.localFilters), {
          params: {
            sortBy: 'container',
            sortDesc: sortDesc,
          },
        })
          .then(response => {
            this.isLoading = false
            this.containers = response.data.data
          })
      }
    },
    selection(data) {
      this.selectedAll = data.selectedAll
      this.selectedContainers = data.selectedContainers
      this.selectedParcels = data.selectedParcels
      this.checkForNotArrivedParcels()
    },
    submit(data) {
      let url = `/v1/companies/${this.shipment.company.id}/shipments/${this.shipment.id}/declarations`
      if (this.resendables) {
        url += '?resendables=1'
      }
      this.$http.post(searchQuery(url, this.localFilters), {
        all: this.selectedAll,
        containers: this.selectedContainers,
        parcels: this.selectedParcels,
        previous_document_type: data.previous_document_type,
        previous_document_category: data.previous_document_category,
        previous_document_reference: data.previous_document_reference,
        send_arrived_to_facility: data.send_arrived_to_facility,
        send_notification_of_arrival: data.send_notification_of_arrival,
        noa_at: data.noa_at,
        warehouse_id: data.warehouse_id,
        has_arrived: data.has_arrived,
        customs_country: data.customs_country,
        declaration_type: this.declarationType,
        inland_mode_of_transport: data.inland_mode_of_transport,
        mode_of_transport_at_border: data.mode_of_transport_at_border,
        transport_registration_number: data.transport_registration_number,
        previous_document_number_of_items: data.previous_document_number_of_items,
      })
        .then(() => {
          EventBus.$emit('refresh-statistics')
          this.selectedAll = false
          this.selectedContainers = []
          this.selectedParcels = []
          this.allParcelsData = []
          EventBus.$emit('clear-selections')
          this.fetchData()

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Declarations sent to customs',
              icon: 'EditIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
    deleteParcel(parcel) {
      this.isLoading = true
      const url = `/v1/companies/${this.shipment.company.id}/shipments/${this.shipment.id}/parcels/${parcel.id}`
      this.$http.delete(url).then(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Parcel successfully deleted',
            icon: 'EditIcon',
            variant: 'success',
          },
        })

        const containerCode = parcel.container
        const containerIndex = this.allParcelsData.findIndex(item => item[containerCode])

        if (containerIndex !== -1) {
          const parcelsInContainer = this.allParcelsData[containerIndex][containerCode]
          this.allParcelsData[containerIndex][containerCode] = parcelsInContainer.filter(p => p.id !== parcel.id)

          const containerToUpdate = this.containers.find(c => c.code === containerCode)
          if (containerToUpdate) {
            // eslint-disable-next-line no-plusplus
            containerToUpdate.parcel_count--
          }

          this.selectedParcels = this.selectedParcels.filter(tNr => tNr !== parcel.tracking_number)
        }
      }).catch(error => {
        handleError(error, this.$toast)
      }).finally(() => {
        this.isLoading = false
      })
    },
    updateParcelsCollection(data) {
      this.allParcelsData = data.fullParcelsData
    },
    checkForNotArrivedParcels() {
      let notArrivedCount = 0
      let parcelHasArrivedEvent = false
      let containerData = null
      let parcelData = null

      this.selectedContainers.map(containerCode => {
        containerData = this.containers.find(container => container.code === containerCode)
        if (containerData.contain_not_arrived_parcels) {
          notArrivedCount += 1
        }
        return notArrivedCount
      })

      this.selectedParcels.map(parcelId => {
        parcelData = this.findParcelInContainer(parcelId)

        if (parcelData) {
          if (!parcelData.events.length) {
            notArrivedCount += 1
          } else {
            parcelHasArrivedEvent = false
            parcelData.events.map(parcelEvent => {
              if (parcelEvent.type === 'notification_of_arrival' || parcelEvent.type === 'arrived_to_facility') {
                parcelHasArrivedEvent = true
              }
              return notArrivedCount
            })
            if (!parcelHasArrivedEvent) {
              notArrivedCount += 1
            }
          }
        }
        return notArrivedCount
      })

      this.selectedContainNotArrivedParcels = notArrivedCount > 0
    },
    findParcelInContainer(parcelId) {
      const foundParcel = this.allParcelsData
        .flatMap(containers => Object.values(containers))
        .flat()
        .find(parcel => parcel.id === parcelId)
      return foundParcel || null
    },
    applyFilters(newFilters) {
      this.localFilters = newFilters
      this.fetchData()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
